import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  LinearProgress,
  Button,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import Client from './../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

class PromotionsOpenClaimsByPromotion extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      data: [],
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.periodMode !== this.props.periodMode) {
      this.loadData();
    }
  }

  loadData = () => {
    const periodMode = this.props.periodMode || 'last_30d';

    let queryData = {
      periodMode: periodMode,
    };

    let data = BackofficeReportsApi.getPromotionsOpenClaims(this.state.client, queryData);

    if (data) {
      this.setState({
        data: data.rows || data || [],
      });
    }
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const cleanValue = String(value).replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.state.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';

    const totalOpenClaims = rows.reduce(
      (sum, row) => sum + Number(row.open_claims || 0),
      0
    );

    const totalConsumedClaims = rows.reduce(
      (sum, row) => sum + Number(row.consumed_claims || 0),
      0
    );

    const totalClaimsCreated = rows.reduce(
      (sum, row) => sum + Number(row.claims_created || 0),
      0
    );

    const weightedUnusedShare = totalClaimsCreated > 0
      ? (totalOpenClaims / totalClaimsCreated) * 100
      : 0;

    const highRiskPromotions = rows.filter(
      (row) => Number(row.unused_share || 0) > 50
    ).length;

    const headers = [
      { key: 'promotion_name', label: 'Promotion' },
      { key: 'open_claims', label: 'Open' },
      { key: 'consumed_claims', label: 'Used' },
      { key: 'claims_created', label: 'Issued' },
      { key: 'unused_share', label: 'Unused %' },
    ];

    const csvRows = [
      ['Open claims by promotion'].map((value) => this.csvValue(value)).join(','),
      ['Period', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total open claims', totalOpenClaims].map((value) => this.csvValue(value)).join(','),
      ['Total used claims', totalConsumedClaims].map((value) => this.csvValue(value)).join(','),
      ['Total issued claims', totalClaimsCreated].map((value) => this.csvValue(value)).join(','),
      ['Weighted unused %', `${weightedUnusedShare.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Promotions with unused % over 50%', highRiskPromotions].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'unused_share') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `open_claims_by_promotion_${periodMode}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.state.data || [];

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.25, sm: 2 },
            '&:last-child': { pb: { xs: 1.25, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Open claims by promotion
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: '0.74rem', sm: '0.82rem' },
                    lineHeight: 1.25,
                  }}
                >
                  Promotions with unused claims still waiting to convert
                </Typography>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                disabled={!rows.length}
                onClick={this.handleExportCsv}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  minWidth: 64,
                  px: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  color: '#111827',
                  borderColor: 'rgba(15, 23, 42, 0.14)',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'rgba(15, 23, 42, 0.24)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0,
                  },
                }}
              >
                CSV
              </Button>
            </Stack>

            <TableContainer
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: '12px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: { xs: 620, md: 720 },
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.7rem', sm: '0.78rem' },
                    py: { xs: 0.7, sm: 0.9 },
                    px: { xs: 0.75, sm: 1 },
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 800,
                    color: '#1a237e',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Promotion</TableCell>
                    <TableCell align="right">Open</TableCell>
                    <TableCell align="right">Used</TableCell>
                    <TableCell align="right">Issued</TableCell>
                    <TableCell align="right">Unused</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => {
                    const unusedShare = Number(row.unused_share || 0);

                    return (
                      <TableRow key={`${row.promotion_iref || 'promo'}-${index}`} hover>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: '#111827',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.promotion_name || 'Unknown'}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: unusedShare > 50 ? '#b91c1c' : '#111827',
                          }}
                        >
                          {thousandsSeparator(Number(row.open_claims || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.consumed_claims || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.claims_created || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right" sx={{ minWidth: 120 }}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                            <Box sx={{ width: 54 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(unusedShare, 100)}
                                sx={{
                                  height: 6,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(96, 125, 139, 0.10)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    bgcolor: unusedShare > 50 ? '#b91c1c' : '#607d8b',
                                  },
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                minWidth: 42,
                                fontWeight: 800,
                                color: unusedShare > 50 ? '#b91c1c' : '#111827',
                              }}
                            >
                              {formatPercent(unusedShare)}
                            </Box>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No open claims data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default PromotionsOpenClaimsByPromotion;
